import { forwardRef } from "react";
import { type ToggleButtonGroupProps } from "react-aria-components";
import {
  CoreButtonGroup,
  type PropsWithOptionalTestID,
} from "@boi/components-core";

import * as S from "./ButtonGroupV2.styled";
import {
  ButtonGroupV2Item,
  type ButtonGroupV2ItemProps,
} from "./ButtonGroupV2Item";
import { ReactAriaProviders } from "../ReactAriaProviders";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTopV2, type InputLabelTopV2Props } from "../InputLabelTopV2";

export type ButtonGroupV2Props = PropsWithOptionalTestID &
  Omit<ToggleButtonGroupProps, "orientation" | "children"> &
  Pick<ButtonGroupV2ItemProps, "typographyVariant"> & {
    labelProps?: Omit<InputLabelTopV2Props, "children">;
    alerts?: InputAlertsProps["alerts"];
    variant?: CoreButtonGroup.Variant;
    items: Omit<
      ButtonGroupV2ItemProps,
      "fullWidth" | "textAlign" | "typographyVariant"
    >[];
  };

const ButtonGroupV2 = forwardRef<HTMLDivElement, ButtonGroupV2Props>(
  (props, ref) => {
    const {
      labelProps,
      alerts,
      variant = CoreButtonGroup.DEFAULT_VARIANT,
      typographyVariant,
      items,
      testID = "button-group",
      ...otherProps
    } = props;

    return (
      <ReactAriaProviders
        externallyLabelledBy={props["aria-labelledby"]}
        externallyDescribedBy={props["aria-describedby"]}
        labelProps={labelProps}
        alerts={alerts}
      >
        {({ labelledBy, describedBy }) => (
          <InputWrapper alert={alerts && <InputAlerts alerts={alerts} />}>
            <InputLabelTopV2 {...labelProps}>
              <S.ToggleButtonGroup
                disallowEmptySelection
                {...otherProps}
                ref={ref}
                $variant={variant}
                orientation={variant === "stacked" ? "vertical" : undefined}
                aria-labelledby={labelledBy}
                aria-describedby={describedBy}
                data-testid={testID}
              >
                {items.map((item) => (
                  <ButtonGroupV2Item
                    key={item.id}
                    {...item}
                    fullWidth={["stretch", "stacked"].includes(variant)}
                    textAlign={variant === "stacked" ? "left" : "center"}
                    typographyVariant={typographyVariant}
                    aria-describedby={describedBy}
                    testID={`${testID}-item-${item.id}`}
                  />
                ))}
              </S.ToggleButtonGroup>
            </InputLabelTopV2>
          </InputWrapper>
        )}
      </ReactAriaProviders>
    );
  }
);

ButtonGroupV2.displayName = "ButtonGroupV2";

export default ButtonGroupV2;
